import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from 'react';

const WillBeNullContext = createContext<{
    willBeNull: boolean;
    setWillBeNull: (willBeNull: boolean) => void;
}>({
    willBeNull: false,
    setWillBeNull: () => null,
});

/*
 * The goal of this provider is to render a null further up the component tree
 * when a deeply nested child will end up rendering null itself.
 *
 * When `isNull` is provided, the provider is externally controlled and
 * `setWillBeNull` calls from children are ignored. This allows a parent
 * component to drive visibility based on data that lives above the provider,
 * avoiding the problem where unmounted children can never reset the state.
 */
export function WillBeNullProvider({
    children,
    handleWillBeNull,
    isNull,
}: {
    children: ReactNode;
    handleWillBeNull: () => void;
    isNull?: boolean;
}) {
    const [willBeNull, _setWillBeNull] = useState(false);
    const setWillBeNull = (b: boolean) => {
        // When externally controlled, ignore child-driven updates
        if (isNull !== undefined) return;
        _setWillBeNull(b);
        if (b) {
            handleWillBeNull();
        }
    };

    const resolved = isNull !== undefined ? isNull : willBeNull;

    useEffect(() => {
        if (isNull) {
            handleWillBeNull();
        }
    }, [isNull, handleWillBeNull]);

    if (resolved) {
        return null;
    }

    return (
        <WillBeNullContext.Provider
            value={{ willBeNull: resolved, setWillBeNull }}
        >
            {children}
        </WillBeNullContext.Provider>
    );
}

export function useWillBeNull() {
    const context = useContext(WillBeNullContext);
    return context;
}
